import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";

const quickLinks = [
  { label: "About Us", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Treatments", href: "/treatments" },
  { label: "Medical Tourism Guide", href: "/guide" },
  { label: "Blog", href: "/blog" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
  { label: "Hospital Partners", href: "/partners" },
];

const treatments = [
  { label: "Modern Medicine", href: "/healthcare/modern-medicine" },
  { label: "Ayurveda", href: "/healthcare/ayurveda" },
  { label: "Wellness", href: "/healthcare/wellness" },
  { label: "Rehabilitation", href: "/healthcare/rehabilitation" },
  { label: "Specialty Clinics", href: "/healthcare/specialty-clinics" },
];

export default function Footer() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin") || location.pathname === "/patient" || location.pathname.startsWith("/patient/") || location.pathname.startsWith("/partner") || location.pathname === "/splash") return null;
  return (
    <footer data-testid="main-footer" className="bg-[#03045e] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <img src="/logo.png" alt="Flarial MediTours" className="w-12 h-12 object-contain" />
              <span className="font-outfit font-semibold text-lg">
                <span className="text-white">Flarial</span> <span className="text-[#8bc53f]">MediTours</span>
              </span>
            </div>
            <p className="text-xs text-slate-400 italic mb-5 tracking-wide">Expert Care. Effortless Journey.</p>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              Connecting international patients with world-class healthcare in Kerala, India.
              Complete medical tourism support from consultation to recovery.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-300 mb-2">
              <Phone className="w-4 h-4 text-[#00A4A9]" />
              <a href="tel:+918891516363" className="hover:text-white transition-colors">+91 88915 16363</a>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300 mb-2">
              <Mail className="w-4 h-4 text-[#00A4A9]" />
              <span>info@flarialmeditours.com</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-slate-300">
              <MapPin className="w-4 h-4 text-[#00A4A9] mt-0.5 flex-shrink-0" />
              <span>
                Flarial Technologies Pvt. Ltd. — Kochi<br />
                Flarial MediTours<br />
                Seat No.3, SBC-2, W Room,<br />
                1st Floor, Thapasya Building,<br />
                Infopark Kochi P.O, Kakkanad — 682042,<br />
                Kerala, India
              </span>
            </div>
          </div>

          <div>
            <h4 className="font-outfit font-semibold text-base mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-300 hover:text-[#00A4A9] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-outfit font-semibold text-base mb-5">Healthcare</h4>
            <ul className="space-y-3">
              {treatments.map((t) => (
                <li key={t.href}>
                  <Link
                    to={t.href}
                    className="text-sm text-slate-300 hover:text-[#00A4A9] transition-colors"
                  >
                    {t.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-outfit font-semibold text-base mb-5">Trust & Safety</h4>
            <ul className="space-y-3 text-sm text-slate-300">
              <li>NABH Accredited Hospitals</li>
              <li>JCI Certified Partners</li>
              <li>GDPR & HIPAA-Aligned Data Handling</li>
              <li>End-to-End Encrypted Records</li>
              <li>24/7 Patient Support</li>
            </ul>
            <div className="mt-6 p-3 bg-white/10 rounded-xl border border-white/10">
              <p className="text-xs text-slate-200 leading-relaxed">
                <span className="font-semibold text-white">We follow GDPR.</span> Your medical data is encrypted end-to-end and handled to international privacy standards — your data is in safe hands.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-400">
            &copy; {new Date().getFullYear()} Flarial MediTours. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-slate-400">
            <span className="hover:text-slate-300 cursor-pointer">Privacy Policy</span>
            <span className="hover:text-slate-300 cursor-pointer">Terms of Service</span>
            <Link to="/patient/login" className="hover:text-slate-300">Patient Portal</Link>
            <Link to="/admin/login" className="hover:text-slate-300">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
